import { FC, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon } from '../../shared/assets/Svg'

export const LANGUAGES = [
  { key: 'en', label: 'EN' },
  { key: 'ru', label: 'RU' },
  { key: 'zh', label: '中文' },
]

const Container = styled.div`
  position: relative;
`

const Trigger = styled.button<{ expanded: boolean }>`
  display: flex;
  align-items: center;
  height: 42px;
  padding: 0 14px;
  border: none;
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  font-weight: 500;
  font-size: 16px;
  color: #232323;

  &:hover {
    background: rgba(35, 35, 35, 0.06);
  }

  svg {
    margin-left: 6px;
    transition: transform 200ms;
    transform: rotate(${({ expanded }) => (expanded ? '180deg' : '0deg')});
  }
`

const List = styled.ul`
  position: absolute;
  top: 48px;
  right: 0;
  z-index: 120;
  min-width: 110px;
  padding: 6px;
  margin: 0;
  list-style: none;
  border-radius: 12px;
  background: #FFF;
  box-shadow: 0 16px 40px -18px rgba(35, 35, 35, 0.4);
`

const Option = styled.li<{ selected: boolean }>`
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: ${({ selected }) => (selected ? 600 : 400)};
  color: ${({ selected }) => (selected ? '#1FC7D4' : '#232323')};

  &:hover {
    background: rgba(35, 35, 35, 0.06);
  }
`

export const LangSelector: FC = () => {
  const { i18n } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const current = LANGUAGES.find((lang) => lang.key === i18n.resolvedLanguage) ?? LANGUAGES[0]

  useEffect(() => {
    if (!expanded) {
      return
    }

    const handleOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setExpanded(false)
      }
    }

    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [expanded])

  const closeAndFocusTrigger = () => {
    setExpanded(false)
    triggerRef.current?.focus()
  }

  const selectLanguage = (key: string) => {
    i18n.changeLanguage(key)
    closeAndFocusTrigger()
  }

  const handleContainerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && expanded) {
      closeAndFocusTrigger()
    }
  }

  const handleOptionKeyDown = (event: React.KeyboardEvent, key: string) => {
    if (event.key === 'Enter') {
      selectLanguage(key)
    } else if (event.key === ' ') {
      event.preventDefault()
      selectLanguage(key)
    }
  }

  return (
    <Container ref={containerRef} onKeyDown={handleContainerKeyDown}>
      <Trigger
        ref={triggerRef}
        type='button'
        expanded={expanded}
        aria-haspopup='listbox'
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      >
        {current.label}
        <ChevronDownIcon fill='#232323'/>
      </Trigger>

      {expanded && (
        <List role='listbox'>
          {LANGUAGES.map((lang) => (
            <Option
              key={lang.key}
              role='option'
              tabIndex={0}
              aria-selected={lang.key === current.key}
              selected={lang.key === current.key}
              onClick={() => selectLanguage(lang.key)}
              onKeyDown={(event) => handleOptionKeyDown(event, lang.key)}
            >
              {lang.label}
            </Option>
          ))}
        </List>
      )}
    </Container>
  )
}
